import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react'
import { Box, Snackbar, Typography } from '@mui/material'
import MuiAlert from '@mui/material/Alert'
import {
  toastTypographySx,
  alertProps,
  getAlertSx,
} from '../styles/toastStyles'

type ToastType = 'success' | 'error' | 'warning' | 'info'

interface ToastPosition {
  vertical: 'top' | 'bottom'
  horizontal: 'left' | 'center' | 'right'
}

interface ToastContextType {
  showToast: (
    message: string,
    type?: ToastType,
    autoHideDuration?: number,
    position?: ToastPosition
  ) => void
  hideToast: () => void
}

// デフォルト値を持つコンテキストを作成
const ToastContext = createContext<ToastContextType>({
  showToast: () => {},
  hideToast: () => {},
})

// コンテキストを使用するためのカスタムフック
export const useToast = () => useContext(ToastContext)

interface ToastProviderProps {
  children: ReactNode
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [type, setType] = useState<ToastType>('info')
  const [duration, setDuration] = useState(3000)
  const [position, setPosition] = useState<ToastPosition>({
    vertical: 'bottom',
    horizontal: 'center',
  })
  // 連続して呼び出した場合の状態管理用
  const [isProcessing, setIsProcessing] = useState(false)

  const showToast = useCallback(
    (
      newMessage: string,
      newType: ToastType = 'info',
      autoHideDuration = 3000,
      newPosition: ToastPosition = {
        vertical: 'bottom',
        horizontal: 'center',
      }
    ) => {
      // 処理中フラグを立てることで連続呼び出しを制御
      if (isProcessing) return

      if (open) {
        setIsProcessing(true)
        setOpen(false)

        // 小さな遅延を加えて状態の更新が反映されるようにする
        window.setTimeout(() => {
          setMessage(newMessage)
          setType(newType)
          setDuration(autoHideDuration)
          setPosition(newPosition)
          setOpen(true)
          setIsProcessing(false)
        }, 100)
      } else {
        setMessage(newMessage)
        setType(newType)
        setDuration(autoHideDuration)
        setPosition(newPosition)
        setOpen(true)
      }
    },
    [open, isProcessing]
  )

  const hideToast = useCallback(() => {
    setOpen(false)
  }, [])

  // 表示時間を管理するEffect
  React.useEffect(() => {
    let timer: number | null = null

    if (open && duration > 0) {
      timer = window.setTimeout(() => {
        hideToast()
      }, duration)
    }

    return () => {
      if (timer) {
        window.clearTimeout(timer)
      }
    }
  }, [open, duration, hideToast])

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      <Snackbar
        anchorOrigin={position}
        open={open}
        autoHideDuration={duration}
        onClose={hideToast}
      >
        {/* Box でラップ: Snackbar の Grow が direction prop を渡すため (MUI Issue #35649) */}
        <Box>
          <MuiAlert
            {...alertProps}
            severity={type}
            onClose={hideToast}
            sx={(theme) => getAlertSx(theme, type)}
          >
            <Typography sx={toastTypographySx}>{message}</Typography>
          </MuiAlert>
        </Box>
      </Snackbar>
    </ToastContext.Provider>
  )
}
